import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import type { ContextBrief } from './types';

interface BriefModalProps {
  brief: ContextBrief | null;
  onClose: () => void;
  onSaved: () => void;
}

const BriefModal: React.FC<BriefModalProps> = ({ brief, onClose, onSaved }) => {
  const isEdit = Boolean(brief?.id);
  const [slug, setSlug] = useState(brief?.slug ?? '');
  const [title, setTitle] = useState(brief?.title ?? '');
  const [body, setBody] = useState(brief?.body ?? '');
  const [priority, setPriority] = useState(brief?.priority ?? 100);
  const [expiresAt, setExpiresAt] = useState(
    brief?.expires_at ? brief.expires_at.slice(0, 16) : '',
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (brief) {
      setSlug(brief.slug);
      setTitle(brief.title);
      setBody(brief.body);
      setPriority(brief.priority);
      setExpiresAt(brief.expires_at ? brief.expires_at.slice(0, 16) : '');
    }
  }, [brief]);

  const handleSave = async () => {
    if (!slug.trim() || !title.trim() || !body.trim()) {
      toast.error('Slug, title, and body are required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        slug: slug.trim(),
        title: title.trim(),
        body: body.trim(),
        status: 'active' as const,
        priority,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        created_via: 'engine_ui',
        updated_at: new Date().toISOString(),
      };

      if (isEdit && brief) {
        const { error } = await supabase
          .from('agent_context_briefs')
          .update(payload)
          .eq('id', brief.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('agent_context_briefs').insert(payload);
        if (error) throw error;
      }

      toast.success(isEdit ? 'Brief updated' : 'Brief created');
      onSaved();
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isEdit ? 'Edit context brief' : 'New context brief'}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Slug
              </label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                disabled={isEdit}
                placeholder="lccto_active_step"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-60"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Priority
              </label>
              <input
                type="number"
                min={1}
                max={999}
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Body
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Expires at (optional)
            </label>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 rounded-md">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BriefModal;
